import React, { useEffect, useState } from 'react';
import { NodeProperty } from '@shared/types/node-properties';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info, AlertTriangle, AlertCircle, CheckCircle, Copy, Check, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWorkflowStore } from '@/stores/workflowStore';
import { chatApi } from '@/lib/api';
import { getBackendUrl } from '@/utils/environment';

interface NoticePropertyRendererProps {
  property: NodeProperty;
  nodeId?: string;
  values?: Record<string, any>;
}

export const NoticePropertyRenderer: React.FC<NoticePropertyRendererProps> = ({ 
  property, 
  nodeId,
  values = {}
}) => {
  console.log('🔍 NoticePropertyRenderer RENDER:', { 
    propertyKey: property.key, 
    nodeId,
    valuesApiKey: values.apiKey,
    allValuesKeys: Object.keys(values)
  });
  
  const noticeType = property.typeOptions?.noticeType || 'info';
  const [dynamicContent, setDynamicContent] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isMasked, setIsMasked] = useState(true);
  const currentWorkflow = useWorkflowStore((state) => state.currentWorkflow);

  // Generate dynamic content for special fields
  useEffect(() => {
    console.log('🔍 NoticePropertyRenderer useEffect:', { 
      propertyKey: property.key,
      nodeId, 
      currentWorkflow: currentWorkflow?.id,
      valuesApiKey: values.apiKey,
      allValues: values
    });
    
    if (!nodeId || !currentWorkflow) return;

    const generateContent = async () => {
      try {
        // Get backend URL using centralized utility
        const backendUrl = getBackendUrl();

        // Generate content based on property key
        if (property.key === 'chatUrl') {
          const mode = values.mode || 'hostedChat';
          if (mode === 'hostedChat') {
            const url = `${backendUrl}/chat/${currentWorkflow.id}`;
            setDynamicContent(url);
          } else {
            setDynamicContent('Webhook URL will be available after workflow activation');
          }
        } else if (property.key === 'webhookUrl') {
          // Generate webhook URL for webhook trigger node
          const customPath = values.path;
          let url: string;
          if (customPath) {
            // Use custom path if provided (prefix: /custom/)
            url = `${backendUrl}/custom${customPath}`;
          } else {
            // Auto-generate URL using workflow ID and node ID
            url = `${backendUrl}/api/webhooks/${currentWorkflow.id}/${nodeId}`;
          }
          setDynamicContent(url);
        } else if (property.key === 'apiKey') {
          // Display API key from values (generated via button click)
          const apiKey = values.apiKey;
          console.log('🔍 NoticePropertyRenderer - API Key check:', { 
            apiKey, 
            hasApiKey: !!apiKey,
            allValues: values 
          });
          
          if (apiKey) {
            setDynamicContent(apiKey);
          } else {
            setDynamicContent('Click "Generate API Key" button to create a secure key');
          }
        } else if (property.key === 'embedCode') {
          const mode = values.mode || 'hostedChat';
          if (mode === 'embeddedChat') {
            // Fetch embed code from backend
            try {
              const response = await chatApi.getEmbedCode(currentWorkflow.id);
              setDynamicContent(response.data.embedCode || 'Embed code will be generated after workflow activation');
            } catch (error) {
              // console.error('Error fetching embed code:', error);
              setDynamicContent('Embed code will be generated after workflow activation');
            }
          } else {
            setDynamicContent(null);
          }
        }
      } catch (error) {
        // console.error('Error generating dynamic content:', error);
      }
    };

    generateContent();
  }, [nodeId, currentWorkflow, property.key, values.mode, values.path, values.apiKey]);

  const handleCopy = async () => {
    if (dynamicContent) {
      try {
        await navigator.clipboard.writeText(dynamicContent);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        // console.error('Failed to copy:', error);
      }
    }
  };

  const getIcon = () => {
    switch (noticeType) {
      case 'warning':
        return <AlertTriangle className="h-4 w-4" />;
      case 'error':
        return <AlertCircle className="h-4 w-4" />;
      case 'success':
        return <CheckCircle className="h-4 w-4" />;
      case 'info':
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const getVariant = () => {
    switch (noticeType) {
      case 'error':
        return 'destructive';
      default:
        return 'default';
    }
  };

  // Determine if this is a copyable field
  const isCopyable = property.key === 'chatUrl' || property.key === 'embedCode' || property.key === 'webhookUrl' || property.key === 'apiKey';
  
  // Determine if this is a maskable field (API keys, secrets)
  const isMaskable = property.key === 'apiKey';
  
  // Get display content (masked or unmasked)
  const getDisplayContent = () => {
    if (!dynamicContent) return null;
    
    if (isMaskable && isMasked && dynamicContent.length > 10) {
      // Show first 8 characters + masked portion
      const prefix = dynamicContent.substring(0, 8);
      return `${prefix}${'*'.repeat(32)}`;
    }
    
    return dynamicContent;
  };
  
  const displayContent = getDisplayContent();

  return (
    <Alert variant={getVariant()} className="my-2">
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
        <div className="flex-1 min-w-0">
          {property.label && <AlertTitle>{property.label}</AlertTitle>}
          {dynamicContent ? (
            <AlertDescription className="space-y-2">
              {property.key === 'embedCode' ? (
                <pre className="bg-muted p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all">
                  {dynamicContent}
                </pre>
              ) : (
                <div className="font-mono text-sm break-all">{displayContent}</div>
              )}
            </AlertDescription>
          ) : (
            property.description && <AlertDescription>{property.description}</AlertDescription>
          )}
        </div>
        <div className="flex-shrink-0 flex gap-1">
          {isMaskable && dynamicContent && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMasked(!isMasked)}
              title={isMasked ? 'Show API key' : 'Hide API key'}
            >
              {isMasked ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
            </Button>
          )}
          {isCopyable && dynamicContent && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              title="Copy to clipboard"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>
    </Alert>
  );
};
